import InlineText from '../../InlineEdit/InlineText';
import './PressMentions.scss';

interface MentionItem {
  '@id'?: string;
  quote: string;
  source: string;
  year?: string;
  url?: string;
}

const DEFAULT_ITEMS: MentionItem[] = [
  {
    quote: 'Une cuisine sincère, ancrée dans le terroir wallon.',
    source: 'Le Soir',
    year: '2023',
  },
  {
    quote: '13/20 — Une adresse à découvrir.',
    source: 'Gault&Millau',
    year: '2022',
  },
];

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const View = (props: any) => {
  const { data, isEditMode, block, onChangeBlock } = props;

  const eyebrow = data.eyebrow ?? (isEditMode ? '' : 'Distinctions');
  const heading = data.heading ?? (isEditMode ? '' : "Ce qu'on dit de nous.");

  const sourceItems: MentionItem[] =
    Array.isArray(data.items) && data.items.length > 0
      ? data.items
      : isEditMode
        ? []
        : DEFAULT_ITEMS;

  const items = sourceItems.map((it) => ({
    ...it,
    quote: it.quote ?? '',
    source: it.source ?? '',
  }));

  const update = (field: string, value: string) =>
    onChangeBlock(block, { ...data, [field]: value });

  const updateItem = (index: number, patch: Partial<MentionItem>) => {
    const next = items.map((it, i) => (i === index ? { ...it, ...patch } : it));
    onChangeBlock(block, { ...data, items: next });
  };

  const addItem = () => {
    const next = [
      ...items,
      { '@id': newId(), quote: '', source: '', year: '', url: '' },
    ];
    onChangeBlock(block, { ...data, items: next });
  };

  const removeItem = (index: number) => {
    const next = items.filter((_, i) => i !== index);
    onChangeBlock(block, { ...data, items: next });
  };

  const renderItem = (item: MentionItem, index: number) => {
    const sourceText = [item.source, item.year].filter(Boolean).join(', ');

    const content = (
      <>
        <cite className="press-source inline-host">
          {isEditMode ? (
            <>
              <InlineText
                value={item.source}
                onChange={(v) => updateItem(index, { source: v })}
                renderTag="span"
                placeholder="Source"
                ariaLabel="Source"
              />
              {item.year !== undefined && (
                <>
                  <span style={{ opacity: 0.5, margin: '0 4px' }}>,</span>
                  <InlineText
                    value={item.year}
                    onChange={(v) => updateItem(index, { year: v })}
                    renderTag="span"
                    placeholder="Année"
                    ariaLabel="Année"
                  />
                </>
              )}
              <button
                type="button"
                aria-label={`Supprimer la mention « ${item.source || 'sans nom'} »`}
                className="inline-remove press-remove"
                onClick={() => removeItem(index)}
              >
                ✕
              </button>
            </>
          ) : (
            sourceText
          )}
        </cite>
        <p className="press-quote">
          {isEditMode ? (
            <InlineText
              value={item.quote}
              onChange={(v) => updateItem(index, { quote: v })}
              renderTag="span"
              placeholder="Citation"
              ariaLabel="Citation"
            />
          ) : (
            item.quote
          )}
        </p>
      </>
    );

    const key = item['@id'] ?? index;

    if (!isEditMode && item.url) {
      return (
        <blockquote className="press-item" key={key}>
          <a href={item.url} target="_blank" rel="noopener noreferrer">
            {content}
          </a>
        </blockquote>
      );
    }

    return (
      <blockquote className="press-item" key={key}>
        {content}
      </blockquote>
    );
  };

  return (
    <section className="press-mentions">
      <div className="shell">
        <div className="press-head">
          {isEditMode ? (
            <>
              <InlineText
                value={eyebrow}
                onChange={(v) => update('eyebrow', v)}
                renderTag="p"
                renderClassName="eyebrow"
                placeholder="Surtitre"
                ariaLabel="Surtitre"
              />
              <InlineText
                value={heading}
                onChange={(v) => update('heading', v)}
                renderTag="h2"
                placeholder="Titre"
                ariaLabel="Titre"
              />
            </>
          ) : (
            <>
              <p className="eyebrow">{eyebrow}</p>
              <h2>{heading}</h2>
            </>
          )}
        </div>

        {items.length > 0 ? (
          <div className="press-grid">{items.map(renderItem)}</div>
        ) : (
          isEditMode && (
            <p
              style={{
                color: 'var(--brume)',
                fontStyle: 'italic',
                margin: '0 0 var(--space-4)',
              }}
            >
              Aucune mention pour le moment. Ajoutez-en une ci-dessous.
            </p>
          )
        )}

        {isEditMode && (
          <button
            type="button"
            className="inline-add inline-add--full press-add"
            onClick={addItem}
          >
            + Ajouter une mention
          </button>
        )}
      </div>
    </section>
  );
};

export default View;
